// =========================================================================
// GOOGLE PLAY BILLING - client side (Android app only).
// Uses the `@capgo/native-purchases` Capacitor plugin that is ALREADY inside the published Animora app
// (checked in the APK: ee.forgr.nativepurchases.NativePurchasesPlugin + com.android.vending.BILLING permission).
// The site is loaded inside the Capacitor WebView, so the plugin is reached through window.Capacitor - no npm import,
// no new APK needed. Works only inside the native app (isNativeApp()); on the website nothing here does anything.
//
// Flow (Boost, one-time):     precheck on our server -> Google Play checkout (NOT consumed yet) -> we send the purchaseToken to
//                             /api/billing/verify -> server verifies with Google + grants -> we consumePurchase() (so it can be bought again).
// Flow (Green Tick, monthly): same, but a subscription (base plan "monthly") and we only acknowledgePurchase().
// A purchase is NEVER consumed/acknowledged before our server said ok, so a failure leaves it pending and it is retried on the
// next app start (Google also auto-refunds purchases that are not acknowledged within 3 days).
// =========================================================================
import { useEffect, useState } from 'react'
import { isNativeApp } from '@/lib/native-app'
import { PLAY_PRODUCTS, PLAY_BOOST_PRODUCTS, PLAY_USERNAME_PRODUCTS, PLAY_COIN_PRODUCTS, PLAY_GREEN_TICK_PRODUCT, findPlayProduct, playProductForUsernameLength } from '@/lib/billing-products'

type Cfg = {
  uid: string
  getIdToken: () => Promise<string>
  onTickGranted?: (premiumUntil: number) => void
  onBoostGranted?: (info: { promotedUntil: number; hours: number }) => void
  onUsernameGranted?: (info: { username: string }) => void
  onCoinsGranted?: (info: { coins: number; again?: boolean }) => void
}

const PENDING_KEY = 'animora_pending_boost'
const PENDING_USERNAME_KEY = 'animora_pending_username'
let cfg: Cfg | null = null
let ready: Promise<boolean> | null = null
const inFlight = new Set<string>()
const prices = new Map<string, string>()

function plugin(): any {
  if (typeof window === 'undefined') return null
  const cap = (window as any).Capacitor
  try {
    if (cap?.Plugins?.NativePurchases) return cap.Plugins.NativePurchases
    if (typeof cap?.isPluginAvailable === 'function' && cap.isPluginAvailable('NativePurchases') && typeof cap.registerPlugin === 'function') return cap.registerPlugin('NativePurchases')
  } catch { /* fall through */ }
  return cap?.Plugins?.NativePurchases || null
}

export function billingAvailable(): boolean {
  return isNativeApp() && !!plugin()
}

async function callVerify(body: Record<string, unknown>): Promise<{ ok: boolean; status: number; data: any }> {
  const token = await cfg!.getIdToken()
  const res = await fetch('/api/billing/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  return { ok: res.ok && data?.ok === true, status: res.status, data }
}

function tokenOf(t: any): string {
  return String(t?.purchaseToken || t?.transaction?.purchaseToken || t?.token || '')
}
function productIdOf(t: any): string {
  return String(t?.productIdentifier || t?.productId || t?.transaction?.productIdentifier || '')
}
function isCancel(e: any): boolean {
  const s = `${e?.code || ''} ${e?.message || e || ''}`.toLowerCase()
  return s.includes('cancel') || s.includes('user_cancel') || s.includes('usercancel')
}

type Processed = { ok: true } | { ok: false; error: string }

/** Verify one owned purchase on our server and, only when the server says ok, consume / acknowledge it. */
async function processPurchase(productId: string, purchaseToken: string): Promise<Processed> {
  const product = findPlayProduct(productId)
  if (!cfg || !product || !purchaseToken) return { ok: false, error: 'purchase-failed' }
  const key = `${product.productId}:${purchaseToken}`
  if (inFlight.has(key)) return { ok: true }
  inFlight.add(key)
  const P = plugin()
  try {
    if (product.kind === 'boost') {
      let ctx: any = null
      try { ctx = JSON.parse(localStorage.getItem(PENDING_KEY) || 'null') } catch { /* ignore */ }
      // No saved target (e.g. app data cleared mid-purchase): leave it unconsumed - nothing is lost, Google refunds
      // after 3 days and the next start retries as soon as a target exists.
      if (!ctx?.id || ctx.productId !== product.productId) return { ok: false, error: 'purchase-failed' }
      const r = await callVerify({ productId, purchaseToken, promotion: { kind: ctx.kind, id: ctx.id, goal: ctx.goal } })
      if (!r.ok) return { ok: false, error: String(r.data?.error || 'purchase-failed') }
      try { localStorage.removeItem(PENDING_KEY) } catch { /* ignore */ }
      try { await P.consumePurchase({ purchaseToken }) } catch (e) { console.error('consume failed', e) } // lets the same Boost be bought again
      cfg.onBoostGranted?.({ promotedUntil: Number(r.data.promotedUntil || 0), hours: Number(r.data.hours || product.hours) })
      return { ok: true }
    }
    if (product.kind === 'coins') {
      // Coins are added by the SERVER only after Google confirms the payment. Consume afterwards so the same pack can be bought again.
      const r = await callVerify({ productId, purchaseToken })
      if (!r.ok) return { ok: false, error: String(r.data?.error || 'purchase-failed') }
      try { await P.consumePurchase({ purchaseToken }) } catch (e) { console.error('consume failed', e) }
      cfg.onCoinsGranted?.({ coins: Number(r.data.coins || 0), again: r.data.again === true })
      return { ok: true }
    }
    if (product.kind === 'username') {
      let ctx: any = null
      try { ctx = JSON.parse(localStorage.getItem(PENDING_USERNAME_KEY) || 'null') } catch { /* ignore */ }
      // No saved handle (app data cleared mid-purchase): leave it unconsumed - Google refunds after 3 days.
      if (!ctx?.username || ctx.productId !== product.productId) return { ok: false, error: 'purchase-failed' }
      const r = await callVerify({ productId, purchaseToken, username: ctx.username })
      if (!r.ok) {
        // A rule refused the handle AFTER payment (taken / locked): nothing is consumed, Google refunds it by itself.
        if (['username-taken', 'username-held', 'username-change-locked', 'same-username'].includes(String(r.data?.error))) { try { localStorage.removeItem(PENDING_USERNAME_KEY) } catch { /* ignore */ } }
        return { ok: false, error: String(r.data?.error || 'purchase-failed') }
      }
      try { localStorage.removeItem(PENDING_USERNAME_KEY) } catch { /* ignore */ }
      try { await P.consumePurchase({ purchaseToken }) } catch (e) { console.error('consume failed', e) } // lets a short handle be bought again later
      cfg.onUsernameGranted?.({ username: String(r.data.username || ctx.username) })
      return { ok: true }
    }
    const r = await callVerify({ productId, purchaseToken })
    if (!r.ok) return { ok: false, error: String(r.data?.error || 'purchase-failed') }
    try { await P.acknowledgePurchase({ purchaseToken }) } catch { /* the server already acknowledged it, or it was auto-acknowledged */ }
    cfg.onTickGranted?.(Number(r.data.premiumUntil || 0))
    return { ok: true }
  } catch (e) {
    console.error('billing process failed', e)
    return { ok: false, error: 'purchase-failed' }
  } finally {
    inFlight.delete(key)
  }
}

const missing = new Set<string>() // ids Google Play did not return even when asked one by one
const ALL_PRICE_GROUPS = (): Array<{ type: 'inapp' | 'subs'; ids: string[] }> => [
  { type: 'inapp', ids: [...PLAY_BOOST_PRODUCTS, ...PLAY_USERNAME_PRODUCTS, ...PLAY_COIN_PRODUCTS].map((p) => p.productId) },
  { type: 'subs', ids: [PLAY_GREEN_TICK_PRODUCT.productId] },
]

function storePrices(res: any) {
  const list: any[] = Array.isArray(res?.products) ? res.products : Array.isArray(res) ? res : []
  for (const p of list) {
    const id = String(p?.identifier || p?.productIdentifier || '')
    const label = String(p?.priceString || '') || (p?.price != null && p?.currencyCode ? `${p.currencyCode} ${p.price}` : '')
    if (id && label) { prices.set(id, label); missing.delete(id) }
  }
}

async function loadPrices(P: any) {
  for (const g of ALL_PRICE_GROUPS()) {
    // 1) one batch for everything of this type (fast)
    try { storePrices(await P.getProducts({ productIdentifiers: g.ids, productType: g.type })) }
    catch (e) { console.error('billing getProducts failed', g.type, e) }
    // 2) anything still without a price is asked for ONE BY ONE - a batch can silently leave products out
    for (const id of g.ids) {
      if (prices.has(id)) continue
      try { storePrices(await P.getProducts({ productIdentifiers: [id], productType: g.type })) }
      catch (e) { console.error('billing getProducts failed for', id, e) }
      if (!prices.has(id)) { missing.add(id); console.warn('Google Play did not return product', id) }
    }
  }
}

let retryTimer: ReturnType<typeof setInterval> | null = null
/** Keeps asking Google Play for products that did not come back (newly created products can take hours to appear). */
function startPriceRetry() {
  if (retryTimer || typeof window === 'undefined') return
  let runs = 0
  retryTimer = setInterval(async () => {
    const P = plugin()
    if (!P || ++runs > 120 || !missing.size) { if (retryTimer) clearInterval(retryTimer); retryTimer = null; return } // ~1 hour max
    for (const g of ALL_PRICE_GROUPS()) {
      for (const id of g.ids) {
        if (prices.has(id)) continue
        try { storePrices(await P.getProducts({ productIdentifiers: [id], productType: g.type })) } catch { /* try again next round */ }
      }
    }
  }, 30000)
}

/** Ask Google Play again right now (e.g. when the Coin Store opens). */
export async function refreshPrices(): Promise<void> {
  const P = plugin()
  if (!P || !isNativeApp()) return
  try { await loadPrices(P); startPriceRetry() } catch (e) { console.error('billing refreshPrices failed', e) }
}

/** Purchases that are owned but were never confirmed (app killed mid-purchase) and Green Tick renewals. */
async function recover(P: any) {
  for (const type of ['inapp', 'subs'] as const) {
    try {
      const res = await P.getPurchases({ productType: type })
      const list: any[] = Array.isArray(res?.purchases) ? res.purchases : Array.isArray(res) ? res : []
      for (const t of list) {
        const productId = productIdOf(t)
        const product = findPlayProduct(productId)
        if (!product || product.type !== type) continue
        await processPurchase(productId, tokenOf(t))
      }
    } catch (e) { console.error('billing getPurchases failed', type, e) }
  }
}

/** Call once after sign-in. Safe to call again (e.g. a different account): it only swaps the config. */
export function startBilling(next: Cfg): Promise<boolean> {
  const first = !ready
  cfg = next
  if (ready) return ready
  ready = (async () => {
    if (!isNativeApp()) return false
    const P = plugin()
    if (!P) { console.warn('@capgo/native-purchases is not installed in the native app'); return false }
    try {
      const sup = await P.isBillingSupported()
      if (sup && sup.isBillingSupported === false) return false
      await loadPrices(P)
      if (missing.size) startPriceRetry()
      void recover(P)
      return true
    } catch (e) {
      console.error('billing init failed', e)
      ready = null
      return false
    }
  })()
  void first
  return ready
}

/** Localized price string from Google Play, e.g. "₹49.00" (null until the store has answered). */
export function playPrice(productId: string): string | null {
  return prices.get(productId) || null
}

export type BuyResult = { ok: true } | { ok: false; cancelled?: boolean; error: string }

async function order(product: (typeof PLAY_PRODUCTS)[number]): Promise<BuyResult> {
  const P = plugin()
  if (!P) return { ok: false, error: 'billing-unavailable' }
  let res: any
  try {
    res = await P.purchaseProduct(
      product.type === 'subs'
        ? { productIdentifier: product.productId, planIdentifier: (product as any).basePlanId, productType: 'subs', quantity: 1, autoAcknowledgePurchases: false }
        : { productIdentifier: product.productId, productType: 'inapp', quantity: 1, isConsumable: true, autoAcknowledgePurchases: false },
    )
  } catch (e: any) {
    if (isCancel(e)) return { ok: false, cancelled: true, error: 'cancelled' }
    const msg = String(e?.message || '').toLowerCase()
    if (msg.includes('not found') || msg.includes('unavailable')) return { ok: false, error: 'product-not-found' }
    return { ok: false, error: String(e?.message || 'payment-failed') }
  }
  const tx = res?.transaction || res
  const done = await processPurchase(product.productId, tokenOf(tx))
  return done.ok ? { ok: true } : { ok: false, error: done.error }
}

/** Start the Boost checkout. `target` = the post/story to boost. The result is delivered through onBoostGranted. */
export async function buyBoost(productId: string, target: { kind: 'post' | 'story'; id: string; goal?: 'profile' | 'website' }): Promise<BuyResult> {
  const product = findPlayProduct(productId)
  if (!cfg || !product || product.kind !== 'boost' || !billingAvailable()) return { ok: false, error: 'billing-unavailable' }
  const pre = await callVerify({ productId, precheck: true, promotion: target })
  if (!pre.ok) return { ok: false, error: String(pre.data?.error || 'precheck-failed') }
  try { localStorage.setItem(PENDING_KEY, JSON.stringify({ productId, ...target })) } catch { /* ignore */ }
  const r = await order(product)
  if (!r.ok && (r.cancelled || r.error === 'product-not-found')) { try { localStorage.removeItem(PENDING_KEY) } catch { /* ignore */ } }
  return r
}

/** Start the premium username checkout (1-4 characters). The handle is saved by the server; the result arrives through onUsernameGranted. */
export async function buyUsername(username: string): Promise<BuyResult & { until?: number }> {
  const name = String(username || '').trim().replace(/^@/, '').toLowerCase()
  const product = playProductForUsernameLength(name.length)
  if (!cfg || !product || !billingAvailable()) return { ok: false, error: 'billing-unavailable' }
  const pre = await callVerify({ productId: product.productId, precheck: true, username: name })
  if (!pre.ok) return { ok: false, error: String(pre.data?.error || 'precheck-failed'), until: Number(pre.data?.until || 0) || undefined }
  try { localStorage.setItem(PENDING_USERNAME_KEY, JSON.stringify({ productId: product.productId, username: name })) } catch { /* ignore */ }
  const r = await order(product)
  if (!r.ok && (r.cancelled || r.error === 'product-not-found')) { try { localStorage.removeItem(PENDING_USERNAME_KEY) } catch { /* ignore */ } }
  return r
}

/** Start a coin pack checkout (Coin Store). Coins are added by the server; the result arrives through onCoinsGranted. */
export async function buyCoins(productId: string): Promise<BuyResult> {
  const product = findPlayProduct(productId)
  if (!cfg || !product || product.kind !== 'coins' || !billingAvailable()) return { ok: false, error: 'billing-unavailable' }
  return order(product)
}

/** Start the Green Tick monthly subscription checkout. The result is delivered through onTickGranted. */
export async function buyGreenTick(): Promise<BuyResult> {
  if (!cfg || !billingAvailable()) return { ok: false, error: 'billing-unavailable' }
  return order(PLAY_GREEN_TICK_PRODUCT)
}

/** "Manage / cancel subscription" page in Google Play. */
export function manageSubscriptionUrl(): string {
  return `https://play.google.com/store/account/subscriptions?sku=${PLAY_GREEN_TICK_PRODUCT.productId}&package=com.animora.social`
}

/** Plain-language message for the error codes the server / plugin can return. */
export function billingErrorText(code: string): string {
  switch (code) {
    case 'boost-coming-soon': return 'Boost is coming soon.'
    case 'already-boosted': return 'This post is already boosted. You can boost it again after it ends.'
    case 'boost-limit-active': return 'You already have the maximum number of active boosts. Wait for one to end.'
    case 'boost-limit-daily': return 'Daily boost limit reached. Try again tomorrow.'
    case 'boost-cooldown': return 'Please wait a few minutes before starting another boost.'
    case 'not-your-post': return 'You can only boost your own posts.'
    case 'username-taken': return 'That username was just taken. Pick another one - you have not been charged.'
    case 'username-held': return 'Someone else is buying that username right now. Try again in a few minutes or pick another one.'
    case 'username-change-locked': return 'You changed your username too many times. Username changes are locked for a while.'
    case 'same-username': return 'That is already your username.'
    case 'wrong-length': return 'This username length does not match the product.'
    case 'billing-unavailable': return 'Google Play purchases are only available in the Animora Android app.'
    case 'product-not-found': return 'This item is not available in Google Play right now. Please try again later.'
    case 'payment-pending': return 'Your payment is pending. It will activate automatically once it completes.'
    case 'token-already-used': return 'This purchase belongs to another account.'
    default: return 'Purchase could not be completed. If money was taken it is returned automatically by Google Play.'
  }
}

/** Live localized Google Play price + whether Google Play has (so far) not returned this product at all. Polls for ~5 minutes. */
export function usePlayPriceState(productId: string): { price: string | null; missing: boolean } {
  const [state, setState] = useState<{ price: string | null; missing: boolean }>(() => ({ price: playPrice(productId), missing: missing.has(productId) }))
  useEffect(() => {
    if (state.price) return
    let tries = 0
    const t = setInterval(() => {
      const next = playPrice(productId)
      const miss = missing.has(productId)
      if (next) { setState({ price: next, missing: false }); clearInterval(t) }
      else {
        setState((c) => (c.missing === miss ? c : { price: null, missing: miss }))
        if (++tries > 300) clearInterval(t)
      }
    }, 1000)
    return () => clearInterval(t)
  }, [productId, state.price])
  return state
}

/** Live localized Google Play price for a product ("₹49.00"). The store answers a moment after start-up, so this polls. */
export function usePlayPrice(productId: string): string | null {
  return usePlayPriceState(productId).price
}
